import moment from "moment-timezone";
import "moment/locale/es";

moment.locale("es");

export const EVENT_TYPE_OPTIONS = [
  { value: "ONLINE", label: "En línea", icon: "mdi-monitor" },
  { value: "IN_PERSON", label: "Presencial", icon: "mdi-map-marker" },
  { value: "HYBRID", label: "Híbrido", icon: "mdi-earth" },
];

export const EVENT_TYPE_LABEL = EVENT_TYPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

export const resolveEventTypeLabel = (value) => EVENT_TYPE_LABEL[value] || value || "—";

export const resolveEventTypeIcon = (value) => {
  const found = EVENT_TYPE_OPTIONS.find((opt) => opt.value === value);
  return found ? found.icon : "mdi-calendar";
};

const unwrap = (item) => (item && item.event ? item.event : item) || {};

export const getEventId = (item) => {
  const ev = unwrap(item);
  return ev._id || (item && item._id) || null;
};

export const getEventTitle = (item) => {
  const ev = unwrap(item);
  return ev.name || "Evento sin título";
};

export const getEventDescription = (item) => {
  const ev = unwrap(item);
  return ev.description || ev.summary || ev.shortDescription || "";
};

export const getEventThumbnail = (item) => {
  const ev = unwrap(item);
  if (!ev) return null;
  return ev.banner || ev.image || ev.thumbnail || ev.cover || null;
};

export const getEventCategory = (item) => {
  const ev = unwrap(item);
  return ev.category || null;
};

export const getEventType = (item) => {
  const ev = unwrap(item);
  return ev.type || null;
};

export const getEventTags = (item) => {
  const ev = unwrap(item);
  if (Array.isArray(ev.tags)) return ev.tags;
  if (typeof ev.tags === "string") return [ev.tags];
  return [];
};

export const getEventLocation = (item) => {
  const ev = unwrap(item);
  if (ev.place) return ev.place;
  if (ev.location && ev.location.name) return ev.location.name;
  if (ev.venue && ev.venue.name) return ev.venue.name;
  return null;
};

export const getEventStartDate = (item) => {
  const ev = unwrap(item);
  return ev.dateConfiguration && ev.dateConfiguration.startDate
    ? ev.dateConfiguration.startDate
    : null;
};

export const getEventEndDate = (item) => {
  const ev = unwrap(item);
  return ev.dateConfiguration && ev.dateConfiguration.endDate
    ? ev.dateConfiguration.endDate
    : null;
};

export const isUpcoming = (item) => {
  const start = getEventStartDate(item);
  if (!start) return false;
  return moment(start).isSameOrAfter(moment());
};

export const isLive = (item) => {
  const start = getEventStartDate(item);
  const end = getEventEndDate(item);
  if (!start) return false;
  const now = moment();
  if (end) {
    return moment(start).isSameOrBefore(now) && moment(end).isSameOrAfter(now);
  }
  return moment(start).isSame(now, "day");
};

export const formatEventDate = (item, format = "DD MMM YYYY") => {
  const start = getEventStartDate(item);
  if (!start) return "";
  return moment(start).format(format);
};

export const formatEventTime = (item) => {
  const start = getEventStartDate(item);
  if (!start) return "";
  return moment(start).format("HH:mm");
};

export const formatEventDateRange = (item) => {
  const start = getEventStartDate(item);
  const end = getEventEndDate(item);
  if (!start) return "";
  const startLabel = moment(start).format("DD MMM");
  if (!end) return startLabel;
  const endLabel = moment(end).format("DD MMM YYYY");
  return `${startLabel} — ${endLabel}`;
};

export const formatEventDateFull = (item) => {
  const start = getEventStartDate(item);
  if (!start) return "";
  const startLabel = moment(start).format("DD MMM");
  const timeLabel = moment(start).format("HH:mm");
  const end = getEventEndDate(item);
  if (end) {
    const endLabel = moment(end).format("DD MMM");
    return `${startLabel} — ${endLabel} · ${timeLabel}h`;
  }
  return `${startLabel} · ${timeLabel}h`;
};

export const resolveEventDetailRoute = (item) => {
  const id = getEventId(item);
  if (!id) return "/member-v2/events";
  return `/member-v2/events/${id}`;
};

export const applyEventFilters = (events, filters) => {
  if (!Array.isArray(events)) return [];
  const search = (filters.search || "").trim().toLowerCase();
  const categories = filters.categories || [];
  const types = filters.types || [];
  const tags = filters.tags || [];
  const from = filters.from || null;
  const to = filters.to || null;

  return events.filter((item) => {
    const ev = unwrap(item);
    if (!ev) return false;
    if (search && !(ev.name || "").toLowerCase().includes(search)) return false;
    if (categories.length > 0 && !categories.includes(ev.category)) return false;
    if (types.length > 0 && !types.includes(ev.type)) return false;
    if (tags.length > 0) {
      const itemTags = getEventTags(item);
      const hasTag = tags.some((t) => itemTags.includes(t));
      if (!hasTag) return false;
    }
    const start = getEventStartDate(item);
    if (from && start && moment(start).isBefore(moment(from))) return false;
    if (to && start && moment(start).isAfter(moment(to).endOf("day"))) return false;
    return true;
  });
};

export const sortEventsByDate = (events, order = "asc") => {
  const list = [...(events || [])];
  list.sort((a, b) => {
    const sa = getEventStartDate(a);
    const sb = getEventStartDate(b);
    if (!sa && !sb) return 0;
    if (!sa) return 1;
    if (!sb) return -1;
    return order === "asc" ? moment(sa).valueOf() - moment(sb).valueOf() : moment(sb).valueOf() - moment(sa).valueOf();
  });
  return list;
};

export const extractEventOptions = (filtersData) => {
  if (!filtersData) return { categories: [], tags: [], types: [] };
  return {
    categories: Array.isArray(filtersData.categories) ? filtersData.categories : [],
    tags: Array.isArray(filtersData.tags) ? filtersData.tags : [],
    types: Array.isArray(filtersData.types) ? filtersData.types : [],
  };
};

export const isEventLockedForUser = (user) => {
  if (!user) return true;
  const status = user.validationStatus;
  return status === "EXPIRED" || status === "INACTIVO";
};
